import type {
  IAssetTemplateDetail,
  IAssetTemplateStatus,
  IAssetTemplateInteraction,
} from '../types';

import {
  checkIsShowBadgeDefault,
  checkTemplateDefaultOnGP,
} from '~/modules/asset-template/helpers';
import { CampaignPickItemHoverActions } from '~/modules/campaign/components/CampaignPickItem';
import { BlockStack, Box, Icon, InlineGrid, InlineStack, Text } from '@shopify/polaris';
import { useAssetTemplateContext } from '~/modules/asset-template/contexts';
import { AlertCircleIcon, BulletIcon } from '@shopify/polaris-icons';
import { ProviderSource, TemplateType } from '~/types/template';
import { AssetTemplateBadge } from './AssetTemplateBadge';
import { isThemePageDraft } from '~/utils/themePage';
import { IThemePageStatus } from '~/types/themePage';
import { lastTimestampAt } from '~/utils/dateTime';
import { getLabelByTemplateType } from '~/utils';
import { useTranslation } from 'react-i18next';
import { GTooltip } from '~/presentation/ui';
import { Fragment, useMemo } from 'react';

interface AssetTemplateItemRendererProps {
  detail: IAssetTemplateDetail;
  status: IAssetTemplateStatus;
  interaction?: IAssetTemplateInteraction;
}
export const AssetTemplateItemRenderer: React.FC<AssetTemplateItemRendererProps> = ({
  detail,
  status,
  interaction,
}) => {
  const { t } = useTranslation();

  // TODO: Refactor this
  const { defaultThemePages, modalAssetTemplate } = useAssetTemplateContext();
  const { isPickControl } = modalAssetTemplate;

  const themePage = defaultThemePages?.find(
    (page) => page.default && page.type === detail.templateInfo.type,
  );
  const isTemplateOrigin = detail?.providerSource === ProviderSource.SHOPIFY_ORIGIN;
  const isShowBadgeDefault = checkIsShowBadgeDefault(detail);
  const isTemplateDefaultOnGP = checkTemplateDefaultOnGP(
    defaultThemePages,
    detail.templateInfo.suffix,
  );

  // Only a product's assignment count is ever split between Active and Unlisted (Price Testing
  // duplicates), so only products get the qualifier — collections and pages have no such split.
  const isProductTemplate = detail.templateInfo.type === TemplateType.PRODUCT;

  const assignedToText = useMemo(() => {
    if (!status.assignedPageCount) {
      return t(isProductTemplate ? 'No active {{pageType}} assigned' : 'No {{pageType}} assigned', {
        pageType: getLabelByTemplateType(detail.templateInfo.type, {
          total: 2,
        }),
      });
    }

    const assignedContent = isTemplateOrigin
      ? isProductTemplate
        ? 'Assigned the rest of active {{pageType}} ({{count}} active {{pageType}})'
        : 'Assigned the rest of {{pageType}} ({{count}} {{pageType}})'
      : isProductTemplate
        ? 'Assigned to {{count}} active {{pageType}}'
        : 'Assigned to {{count}} {{pageType}}';

    return t(assignedContent, {
      count: status.assignedPageCount,
      pageType: getLabelByTemplateType(detail.templateInfo.type, {
        total: status.assignedPageCount,
      }),
    });
  }, [isTemplateOrigin, isProductTemplate, detail.templateInfo.type, status.assignedPageCount, t]);

  const isHidePageAssigned = interaction?.hidePageAssigned || status.isHomePage;

  const renderSecondaryText = (text: React.ReactNode | string, isShowDot?: boolean) => (
    <Fragment>
      {isShowDot && (
        <div className="hidden md:block">
          <Box padding="0">
            <Icon tone="subdued" source={BulletIcon} />
          </Box>
        </div>
      )}
      <Text variant="bodyMd" as="p">
        {text}
      </Text>
    </Fragment>
  );

  return (
    <InlineGrid columns={{ xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) auto' }} gap="150">
      <BlockStack gap="150">
        <InlineStack gap="200" wrap={false} blockAlign="center">
          <div className="min-w-0">
            <Text
              variant="headingMd"
              fontWeight="bold"
              as="h3"
              truncate
              {...(interaction?.isDisabled && {
                tone: 'disabled',
              })}
            >
              {detail.label}
            </Text>
          </div>

          {isShowBadgeDefault && (
            <div className="max-w-[50%] md:max-w-[400px]">
              <AssetTemplateBadge
                tone="info"
                tooltip={themePage?.name}
                truncate
                label={t('Default')}
              />
            </div>
          )}

          {isTemplateDefaultOnGP && (
            <div className="shrink-0">
              <GTooltip
                content={t('This template is also assigned to another {{type}}', {
                  type: getLabelByTemplateType(detail.templateInfo.type),
                })}
                maxWidth="extra-large"
              >
                <Icon source={AlertCircleIcon} />
              </GTooltip>
            </div>
          )}

          <AssetTemplateBadge
            show={!isPickControl && isThemePageDraft(detail.status, detail.publishedAt)}
            tone="info"
            label={t('Draft')}
          />
          <AssetTemplateBadge
            show={detail.status === IThemePageStatus.NEED_PUBLISHING}
            tone="attention"
            label={t('Need republishing')}
          />
          <AssetTemplateBadge
            show={status.isThemePageChildren}
            tone="info"
            label={t('GemX only')}
            tooltip={t("This is a GemX-only template and won't appear in GemPages.")}
          />
        </InlineStack>

        <div className="flex flex-col md:flex-row md:items-center md:gap-1">
          {!isHidePageAssigned && renderSecondaryText(assignedToText)}
          {interaction?.helpText && renderSecondaryText(interaction?.helpText, !isHidePageAssigned)}
          {!interaction?.helpText &&
            detail.updatedAt &&
            renderSecondaryText(
              t('Last modified {{date}}', {
                date: lastTimestampAt(detail.updatedAt),
              }),
              !isHidePageAssigned,
            )}
        </div>

        {/* {status.isOverriddenByGemPages && (
          <div className="max-w-fit">
            <Banner tone="warning" children={t('This template was overridden by GemPages')} />
          </div>
        )} */}
      </BlockStack>

      <div className="hidden md:flex md:h-full md:items-center">
        <CampaignPickItemHoverActions actions={interaction?.shortcutActions} />
      </div>
    </InlineGrid>
  );
};
